import type { Unit, Pixel } from '@/data';
import { pageToPageData } from '@/http-client/utils/pageToPageData';
import { getGeneratedPositions, pixelByOnset } from '@/http-client/renderServices/StructureService';
import MetadataParser from '../MetadataParser';
import type { Image3DRowFormat, Image3DSyncFormat } from '../UnitParser';
import UnitParser from '../UnitParser';
import { Structure } from '../../data/Structure';
import type { StructureFormat } from './StructureFormat';

export default class StructureParser {
  private static rowHeight = 150 as Pixel;

  static structureFormatFromNewFormat(sourceFormat: Map<string, unknown>): StructureFormat {
    return {
      info: sourceFormat.get('text') as string,
      license: sourceFormat.get('license') as string,
      contributors: sourceFormat.get('contributors') as Record<string, string>,
      id: sourceFormat.get('id') as string,
      name: sourceFormat.get('name') as string,
      year: sourceFormat.get('year') as string,
    };
  }

  static fromFormat(
    format: StructureFormat,
  ): Structure {
    const name = format?.name;

    const id = format?.id;

    const positions: Image3DSyncFormat = getGeneratedPositions(
      StructureParser.rowHeight,
    );
    const pixelUnit: Unit<Pixel> = UnitParser.createImage3DUnit(positions);

    const width: Pixel = StructureParser.calculateMaxWidthInFirstPage(positions);
    const height = StructureParser.calculateTotalHeightInFirstPage(positions);
    const pages = positions.pages.map((page) => pageToPageData(page, { height, width }));

    const metadata = MetadataParser.fromFormat(format);

    const rowsInFirstPage = positions.pages[0].rows;
    const svgImage = StructureParser.generateSvgFromRows(rowsInFirstPage);

    return new Structure({
      name, id, pixelUnit, width, height, pages, metadata, svgImage,
    });
  }

  private static calculateMaxWidthInFirstPage(positions: Image3DSyncFormat): Pixel {
    const oneOnsetMargin = pixelByOnset;

    const widthMaxInRowsInFirstPage = positions.pages[0].rows
      .map((row) => row['end-x'])
      .reduce((prev, curr) => Math.max(prev, curr));

    return (widthMaxInRowsInFirstPage + oneOnsetMargin) as Pixel;
  }

  private static calculateTotalHeightInFirstPage(positions: Image3DSyncFormat) {
    const nbrRowsFirstPage = positions.pages[0].rows.length;

    return (StructureParser.rowHeight * (nbrRowsFirstPage + 1)) as Pixel;
  }

  private static generateSvgFromRows(rows: Image3DRowFormat[]) {
    return rows
      .map(StructureParser.getLinesInfoToGenerateSVG)
      .map((value) => StructureParser.generateLine(value.cellX, value.y, value.endX))
      .join();
  }

  private static getXInTheRow(row: Image3DRowFormat) {
    return row['onset-x'].map((XOnset) => XOnset.x);
  }

  private static getLinesInfoToGenerateSVG(row: Image3DRowFormat) {
    const lastXOnsetIndex = row['onset-x'].length - 1;
    const endX = row['onset-x'][lastXOnsetIndex].x;
    return {
      cellX: StructureParser.getXInTheRow(row),
      y: row.y,
      endX,
    };
  }

  private static generateLine(xList: number[], yLine:number, endX: number) {
    return xList.map((x, index, array) => {
      const nextX = array[index + 1] || endX;
      const width = nextX - x;
      if (width !== 0) {
        return StructureParser.generateCell(x, yLine, width, StructureParser.rowHeight);
      }
      return '';
    }).join();
  }

  private static generateCell(x: number, y:number, width: number, height: number): string {
    const verticalPadding = 0;
    const xShiftToAlignWithLabels = 0;
    return `<rect x="${x - xShiftToAlignWithLabels}" y="${y - verticalPadding}" width="${width}" height="${height + 2 * verticalPadding}" class="structure"></rect>`;
  }
}
